import { useState, useEffect } from 'react';
import { useQuestionnaireStep } from '../../../../wrappers/question_wrapper/step_context';
import { defaultComponentCopy } from '../../../../helpers/questionnaire_translations';

import React from 'react';
import QuestionWrapper from '../../ui/question_wrapper';
import QuestionHeading from '../../ui/question_heading';
import QuestionSubheading from '../../ui/question_subheading';
import DateInput from '../../ui/date_input';
import ErrorMessage from '../../../ui_kit/utilities/error_message';

const isoToMasked = (value) => {
  if (!value) return '';
  const str = String(value);
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const masked = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (masked) return str;
  return '';
};

const maskedToIso = (masked) => {
  if (!masked) return '';
  const parts = masked.split('/');
  if (parts.length !== 3) return '';
  const [mm, dd, yyyy] = parts;
  if (mm.length !== 2 || dd.length !== 2 || yyyy.length !== 4) return '';
  return `${yyyy}-${mm}-${dd}`;
};

const validateMaskedDate = (masked, messages) => {
  if (!masked || !/^\d{2}\/\d{2}\/\d{4}$/.test(masked)) {
    return { valid: false, error: '' };
  }

  const [mm, dd, yyyy] = masked.split('/').map(Number);

  if (mm < 1 || mm > 12) {
    return { valid: false, error: messages.month_invalid };
  }

  if (dd < 1 || dd > 31) {
    return { valid: false, error: messages.day_invalid };
  }

  // Round-trip catches impossible combinations like 02/30 or 04/31.
  const date = new Date(yyyy, mm - 1, dd);
  if (date.getMonth() !== mm - 1 || date.getDate() !== dd || date.getFullYear() !== yyyy) {
    return { valid: false, error: messages.impossible_date };
  }

  if (yyyy < 1900) {
    return { valid: false, error: messages.year_too_early };
  }

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (date > startOfToday) {
    return { valid: false, error: messages.future_date };
  }

  return { valid: true, error: '' };
};

const yearFromValue = (value) => {
  if (value == null) return '';
  const str = String(value).trim();
  if (/^\d{4}$/.test(str)) return str;
  const iso = str.match(/^(\d{4})-\d{2}-\d{2}/);
  return iso ? iso[1] : '';
};

const validateYear = (year, messages) => {
  if (!/^\d{4}$/.test(year || '')) {
    return { valid: false, error: '' };
  }

  const yyyy = Number(year);
  if (yyyy < 1900) {
    return { valid: false, error: messages.year_too_early };
  }

  const today = new Date();
  if (yyyy > today.getFullYear()) {
    return { valid: false, error: messages.future_year };
  }

  return { valid: true, error: '' };
};

const YearOfBirthQuestion = ({
  heading,
  value,
  onChange,
  onSubmit,
  onBack,
  current,
  total,
  showNavButtons = true,
  subHeading = '',
  placeholder,
  messages,
}) => {
  const text = { ...defaultComponentCopy('date_of_birth', { yearOfBirth: true }), ...messages };
  const resolvedPlaceholder = placeholder || text.year_placeholder;
  const step = useQuestionnaireStep();
  const resolvedCurrent = current ?? step.current;
  const resolvedTotal = total ?? step.total;

  const [year, setYear] = useState(() => yearFromValue(value));

  useEffect(() => {
    setYear(yearFromValue(value));
  }, [value]);

  const handleChange = (next) => {
    setYear(next);
    const validation = validateYear(next, text);
    if (validation.valid && typeof onChange === 'function') {
      onChange(next);
    }
  };

  const validation = validateYear(year, text);
  const isDisabled = !validation.valid;
  const showError = year.length === 4 && !validation.valid;

  return (
    <QuestionWrapper
      current={resolvedCurrent}
      total={resolvedTotal}
      onBack={onBack}
      onSubmit={onSubmit}
      isDisabled={isDisabled}
      showNavButtons={showNavButtons}
    >
      <QuestionHeading>{heading}</QuestionHeading>
      {subHeading && <QuestionSubheading>{subHeading}</QuestionSubheading>}
      <DateInput yearOnly value={year} onChange={handleChange} placeholder={resolvedPlaceholder} ariaLabel={heading} />
      {showError && <ErrorMessage error={validation.error} />}
    </QuestionWrapper>
  );
};

const FullDateOfBirthQuestion = ({
  heading,
  value,
  onChange,
  onSubmit,
  onBack,
  current,
  total,
  showNavButtons = true,
  subHeading = '',
  placeholder,
  messages,
}) => {
  const text = { ...defaultComponentCopy('date_of_birth'), ...messages };
  const resolvedPlaceholder = placeholder || text.date_placeholder;
  const step = useQuestionnaireStep();
  const resolvedCurrent = current ?? step.current;
  const resolvedTotal = total ?? step.total;

  const [masked, setMasked] = useState(() => isoToMasked(value));

  useEffect(() => {
    setMasked(isoToMasked(value));
  }, [value]);

  const handleChange = (next) => {
    setMasked(next);
    const validation = validateMaskedDate(next, text);
    if (validation.valid && typeof onChange === 'function') {
      onChange(maskedToIso(next));
    }
  };

  const validation = validateMaskedDate(masked, text);
  const isDisabled = !validation.valid;
  const showError = masked.length === 10 && !validation.valid;

  return (
    <QuestionWrapper
      current={resolvedCurrent}
      total={resolvedTotal}
      onBack={onBack}
      onSubmit={onSubmit}
      isDisabled={isDisabled}
      showNavButtons={showNavButtons}
    >
      <QuestionHeading>{heading}</QuestionHeading>
      {subHeading && <QuestionSubheading>{subHeading}</QuestionSubheading>}
      <DateInput value={masked} onChange={handleChange} placeholder={resolvedPlaceholder} ariaLabel={heading} />
      {showError && <ErrorMessage error={validation.error} />}
    </QuestionWrapper>
  );
};

const DateOfBirthQuestion = (props) =>
  props.yearOnly ? <YearOfBirthQuestion {...props} /> : <FullDateOfBirthQuestion {...props} />;

export default DateOfBirthQuestion;
